import { useState } from 'react';
import { View, StyleSheet, Pressable, Modal as RNModal } from 'react-native';
import { NavigationList, type NavigationListItemData, Text, Icon, theme } from '@socrait/react';

import { ErrorBox } from '~/components/ErrorBox';
import { MainLayout } from '~/layouts/MainLayout';
import { Period, Student } from '~/services/types';
import { StudentDataScreen } from '~/screens/studentData';

type RosterScreenProps = {
  onClose: () => void;
  activePeriod: Period | undefined;
};

export function RosterScreen({ onClose, activePeriod }: RosterScreenProps) {
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  if (!activePeriod) {
    return (
      <MainLayout onAfterPeriodSelect={onClose}>
        <View style={[styles.container, styles.errorContainer]}>
          <Pressable onPress={onClose} style={[styles.closeButton, styles.errorCloseButton]}>
            <Icon type="close" />
          </Pressable>
          <ErrorBox text="Could not load the period data. Please close this window and try again." />
        </View>
      </MainLayout>
    );
  }

  const students = [...(activePeriod.students || [])].sort((a, b) => a.voiceName.localeCompare(b.voiceName));

  const studentItems: NavigationListItemData[] = students.map((student) => ({
    id: student.id,
    label: student.voiceName,
    onPress: () => {
      setSelectedStudent(student);
      setStudentModalOpen(true);
    },
    telemetryLabel: `roster-student-${student.id}`,
  }));

  return (
    <>
      <MainLayout onAfterPeriodSelect={onClose}>
        <View style={styles.container}>
          <View style={styles.header}>
            <View style={styles.headerTop}>
              <Text style={styles.periodName}>{activePeriod.name}</Text>
              <Pressable onPress={onClose} style={styles.closeButton}>
                <Icon type="close" />
              </Pressable>
            </View>
            <Text style={styles.studentCount}>
              {students.length} student{students.length !== 1 ? 's' : ''} in roster
            </Text>
          </View>
          <View style={styles.content}>
            <NavigationList containerStyle={styles.studentList} items={studentItems} maxColumns={4} />
          </View>
        </View>
      </MainLayout>
      <RNModal visible={studentModalOpen} onRequestClose={() => setStudentModalOpen(false)} animationType="none">
        {selectedStudent && (
          <StudentDataScreen
            activePeriod={activePeriod}
            student={selectedStudent}
            students={students}
            onClose={() => setStudentModalOpen(false)}
            onAfterPeriodSelect={() => {
              setStudentModalOpen(false);
              onClose();
            }}
          />
        )}
      </RNModal>
    </>
  );
}

const styles = StyleSheet.create({
  errorContainer: {
    padding: theme.spacing.xxlarge,
    gap: theme.spacing.xlarge,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  header: {
    borderBottomColor: theme.colors.gray,
    borderBottomWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxlarge,
    paddingHorizontal: theme.spacing.xlarge * 2,
    paddingTop: theme.spacing.xxlarge,
    paddingBottom: theme.spacing.large,
  },
  headerTop: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  periodName: {
    fontSize: theme.fontSizes.large,
    fontWeight: theme.fontWeights.medium,
  },
  studentCount: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
  },
  closeButton: {
    padding: theme.spacing.medium,
  },
  errorCloseButton: {
    alignSelf: 'flex-end',
  },
  studentList: {
    borderRadius: 0,
  },
  content: {
    flex: 1,
    paddingHorizontal: theme.spacing.xlarge * 2,
    overflowY: 'auto',
  },
});
